import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { defaultInstance } from "../api/httpClient";
import { toaster } from "baseui/toast";
import { fetchAllPages } from "../utils/fetchAllPages";

export interface ActionLinkObject {
  id?: string | number;
  action?: string; // 'business' | 'article' | 'url'
}

export interface Ad {
  id?: number;
  title_es: string;
  title_en: string;
  short_description_es: string;
  short_description_en: string;
  art_img_url: string;
  background_color: string;
  action_link_object: ActionLinkObject | null;
  status: 'draft' | 'published';
  priority: number;
  publish_date: string;
  expiration_date: string;
  user_types_ids: number[];
}

export const useAds = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['ads'],
    queryFn: () => fetchAllPages<Ad>('/ads'),
  });

  return {
    ads: (data as Ad[] | undefined) || [],
    isLoading,
    error,
  };
};

export const useCreateAd = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Ad>) => {
      return await defaultInstance({
        url: '/ads',
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      toaster.positive('Ad created successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to create ad', { autoHideDuration: 3000 });
    },
  });
};

export const useUpdateAd = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Ad> }) => {
      return await defaultInstance({
        url: `/ads/${id}`,
        method: 'PUT',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      toaster.positive('Ad updated successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to update ad', { autoHideDuration: 3000 });
    },
  });
};

export const useDeleteAd = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      return await defaultInstance({
        url: `/ads/${id}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      toaster.positive('Ad deleted successfully', { autoHideDuration: 3000 });
    },
    onError: (error: any) => {
      toaster.negative(error?.message || 'Failed to delete ad', { autoHideDuration: 3000 });
    },
  });
};

/** SAM-1095: the swipe order of the Featured carousel. `order` = ad ids, first = first card; priority becomes 1..n. */
export const useSetAdsOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changes: { id: number; priority: number }[]) => {
      const results = await Promise.allSettled(
        changes.map(({ id, priority }) => defaultInstance({ url: `/ads/${id}`, method: 'PATCH', data: { priority } })),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed) throw new Error(`${failed} of ${changes.length} ads could not be reordered`);
    },
    onError: (error: Error) => {
      toaster.negative(error?.message || 'Failed to reorder ads', { autoHideDuration: 3000 });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['ads'] }),
  });
};

/** Ads that need a new priority so that `orderedIds` reads 1..n; ads already in place are left alone. */
export const adOrderChanges = (ads: Pick<Ad, 'id' | 'priority'>[], orderedIds: number[]): { id: number; priority: number }[] => {
  const current = new Map(ads.map((a) => [a.id, a.priority]));
  return orderedIds
    .map((id, i) => ({ id, priority: i + 1 }))
    .filter(({ id, priority }) => current.get(id) !== priority);
};
